import { FloorGuideType } from '@/enums/floor-guide';
import type { KioskSelectedCategory } from '@/services/dto/kiosk-config';
import { useFloorGuideStore } from '@/stores/floorGuideStore';
import type { SelectCategoryOptions } from '@/types/floor-guide';
import { useEffect, useRef } from 'react';

type UseExternalCategoryNavigationParams = {
    handleSelectCategory: (category: KioskSelectedCategory, options?: SelectCategoryOptions) => Promise<void>;
    isMapReady: boolean;
    isDestinationIconReady: boolean;
};

export const useExternalCategoryNavigation = ({
    handleSelectCategory,
    isMapReady,
    isDestinationIconReady,
}: UseExternalCategoryNavigationParams): void => {
    const { selectedCategoryNavigation, resetFloorGuideType, resetSelectedCategoryNavigation } = useFloorGuideStore();
    const processedCategoryRef = useRef<string | null>(null);
    const handleSelectCategoryRef = useRef(handleSelectCategory);
    handleSelectCategoryRef.current = handleSelectCategory;

    useEffect(() => {
        const run = async (): Promise<void> => {
            if (!selectedCategoryNavigation || !isMapReady || !isDestinationIconReady) {
                return;
            }
            const currentCategoryKey = `${selectedCategoryNavigation.categoryCode}-${FloorGuideType.CATEGORY}`;
            if (processedCategoryRef.current === currentCategoryKey) {
                return;
            }
            processedCategoryRef.current = currentCategoryKey;
            const options: SelectCategoryOptions = {
                autoSwitchToNearestFloor: true,
                syncMapCategoryBar: false,
            };
            try {
                await handleSelectCategoryRef.current(selectedCategoryNavigation, options);
            } finally {
                resetSelectedCategoryNavigation();
                resetFloorGuideType();
                processedCategoryRef.current = null;
            }
        };
        void run();
    }, [
        selectedCategoryNavigation,
        isMapReady,
        isDestinationIconReady,
        resetFloorGuideType,
        resetSelectedCategoryNavigation,
    ]);
};
